import React, { useState, useEffect } from 'react';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { api } from '../../../../lib/api';

const API = "/api/system/glow/smtp-settings";

/** 자주 쓰는 SMTP 호스트 예시 (선택 시 host/port/STARTTLS 자동 입력) */
const SMTP_HOST_PRESETS = [
  { value: '', label: '직접 입력', host: '', port: '', starttlsEnable: true },
  { value: 'gmail', label: 'Gmail (smtp.gmail.com)', host: 'smtp.gmail.com', port: '587', starttlsEnable: true },
  { value: 'naver', label: 'Naver (smtp.naver.com)', host: 'smtp.naver.com', port: '587', starttlsEnable: true },
  { value: 'daum', label: 'Daum (smtp.daum.net)', host: 'smtp.daum.net', port: '465', starttlsEnable: false },
  { value: 'nate', label: 'Nate (smtp.mail.nate.com)', host: 'smtp.mail.nate.com', port: '587', starttlsEnable: true },
];

const matchPresetByHost = (host) => {
  const h = (host || '').trim().toLowerCase();
  if (!h) return '';
  const found = SMTP_HOST_PRESETS.find((p) => p.host && p.host.toLowerCase() === h);
  return found?.value || '';
};

const emptyForm = () => ({
  alias: '',
  host: '',
  port: '',
  username: '',
  password: '',
  senderName: '',
  starttlsEnable: true,
  smtpAuth: true,
  connectionTimeoutMs: 5000,
});

/**
 * SMTP 설정 등록/수정 폼 팝업
 * config.initialData: 수정 시 기존 row 데이터 (없으면 신규)
 * resolve: { submit: true, data } | { submit: false } | { activated: true } | { deleted: true }
 *
 * 전역 openConfirm/openAlert는 단일 팝업이라 이 모달을 교체·Promise를 끊으므로
 * 확인은 모달 내부 오버레이로 처리한다. (ConsortiumMemberEditPopup 과 동일)
 */
const SmtpSettingsFormPopup = ({ config, onClose }) => {
  const isEdit = Boolean(config?.initialData?.uid);
  const editUid = config?.initialData?.uid || null;
  const isActive = Boolean(config?.initialData?.active);
  const displayName = config?.initialData?.alias || config?.initialData?.host || '';

  const [form, setForm] = useState(emptyForm());
  const [hostPreset, setHostPreset] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTestMail, setSendingTestMail] = useState(false);
  const [testToEmail, setTestToEmail] = useState('');
  const [testMsg, setTestMsg] = useState({ ok: null, text: '' });
  const [formError, setFormError] = useState('');
  const [validated, setValidated] = useState(false);
  /** null | 'activate' | 'delete' — 전역 confirm 대신 내부 확인 */
  const [confirmKind, setConfirmKind] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // 초기 데이터 설정
  useEffect(() => {
    if (config?.initialData) {
      const d = config.initialData;
      setForm({
        alias: d.alias || '',
        host: d.host || '',
        port: d.port != null ? String(d.port) : '',
        username: d.username || '',
        password: '',
        senderName: d.senderName || '',
        starttlsEnable: d.starttlsEnable !== false,
        smtpAuth: d.smtpAuth !== false,
        connectionTimeoutMs: d.connectionTimeoutMs || 5000,
      });
      setHostPreset(matchPresetByHost(d.host));
    } else {
      setForm(emptyForm());
      setHostPreset('');
    }
    setFormError('');
    setTestMsg({ ok: null, text: '' });
    setTestToEmail(config?.initialData?.username || '');
    setValidated(false);
    setConfirmKind(null);
    setConfirmBusy(false);
  }, [config?.initialData]);

  useEffect(() => {
    if (!confirmKind) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!confirmBusy) setConfirmKind(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmKind, confirmBusy]);

  const onChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'host') {
      setHostPreset(matchPresetByHost(value));
    }
  };

  const onHostPresetChange = (e) => {
    const value = e.target.value;
    setHostPreset(value);
    const preset = SMTP_HOST_PRESETS.find((p) => p.value === value);
    if (!preset || !preset.host) return;
    setForm((prev) => ({
      ...prev,
      host: preset.host,
      port: preset.port,
      starttlsEnable: preset.starttlsEnable,
    }));
  };

  const buildPayload = () => ({
    alias: form.alias.trim(),
    host: form.host.trim(),
    port: form.port ? Number(form.port) : 587,
    username: form.username.trim(),
    password: form.password,
    senderName: form.senderName.trim(),
    starttlsEnable: Boolean(form.starttlsEnable),
    smtpAuth: Boolean(form.smtpAuth),
    connectionTimeoutMs: Number(form.connectionTimeoutMs) || 5000,
  });

  // ── 연결 테스트 ───────────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true);
    setFormError('');
    setTestMsg({ ok: null, text: '' });
    try {
      const payload = { ...buildPayload(), existingUid: editUid };
      const res = await api.post(`${API}/test`, payload);
      setTestMsg({ ok: Boolean(res?.ok), text: res?.message || '' });
      if (!res?.ok) setFormError(res?.message || '연결 테스트 실패');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '연결 테스트 실패';
      setTestMsg({ ok: false, text: msg });
      setFormError(msg);
    } finally {
      setTesting(false);
    }
  };

  // ── 테스트 메일 발송 ──────────────────────────────────────────────────────
  const handleSendTestMail = async () => {
    const to = testToEmail.trim();
    if (!to || !to.includes('@')) {
      setFormError('테스트 메일 수신 주소를 입력해 주세요.');
      return;
    }
    setSendingTestMail(true);
    setFormError('');
    setTestMsg({ ok: null, text: '' });
    try {
      const payload = { ...buildPayload(), existingUid: editUid, toEmail: to };
      const res = await api.post(`${API}/test`, payload);
      setTestMsg({ ok: Boolean(res?.ok), text: res?.message || '' });
      if (!res?.ok) setFormError(res?.message || '테스트 메일 발송 실패');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '테스트 메일 발송 실패';
      setTestMsg({ ok: false, text: msg });
      setFormError(msg);
    } finally {
      setSendingTestMail(false);
    }
  };

  // ── 저장 ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.alias.trim() || !form.host.trim() || !form.username.trim()) {
      setValidated(true);
      setFormError('이름, 호스트, 발송 계정은 필수 항목입니다.');
      return;
    }
    setSaving(true);
    setFormError('');
    setTestMsg({ ok: null, text: '' });
    try {
      const payload = buildPayload();
      if (editUid) {
        await api.put(`${API}/${editUid}`, payload);
      } else {
        await api.post(API, payload);
      }
      onClose(true, { data: payload });
    } catch (e) {
      const msg = e?.response?.data?.message || '저장 실패';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── 활성 전환 / 삭제 (내부 confirm — 전역 openConfirm 사용 금지) ───────────
  const handleActivateClick = () => setConfirmKind('activate');
  const handleDeleteClick = () => setConfirmKind('delete');
  const handleConfirmCancel = () => {
    if (confirmBusy) return;
    setConfirmKind(null);
  };

  const handleConfirmApply = async () => {
    if (!confirmKind || !editUid || confirmBusy) return;
    setConfirmBusy(true);
    setFormError('');
    try {
      if (confirmKind === 'activate') {
        await api.patch(`${API}/${editUid}/activate`);
        onClose(true, { activated: true });
        return;
      }
      await api.delete(`${API}/${editUid}`);
      onClose(true, { deleted: true });
    } catch (e) {
      setFormError(e?.response?.data?.message || (confirmKind === 'activate' ? '활성 전환 실패' : '삭제 실패'));
      setConfirmKind(null);
      setConfirmBusy(false);
    }
  };

  const confirmTitle = confirmKind === 'delete' ? '삭제 확인' : '활성 전환 확인';
  const confirmContent = confirmKind === 'delete'
    ? `"${displayName}" 설정을 삭제하겠습니까?`
    : `"${displayName}" 설정을 활성 SMTP로 전환하겠습니까?\n현재 활성 설정은 비활성으로 전환됩니다.`;
  const confirmPrimaryLabel = confirmKind === 'delete' ? '삭제' : '활성 전환';
  const confirmPrimaryClass = confirmKind === 'delete' ? 'btn-danger' : 'btn-primary';

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-smtp-settings-form"
      id="smtp-settings-form-popup"
    >
      <div className="main-popup-body">
        <div className="main-popup-form">
          <div className="alert alert-light border mb-3 small">
            <strong>안내</strong>
            <ul className="mb-0 ps-3 mt-1 text-muted">
              <li>Gmail은 &quot;앱 비밀번호&quot;를 사용하세요 (계정 비밀번호 불가).</li>
              <li>Daum(포트 465)은 SSL 연결입니다. 메일함에서 IMAP/POP3·SMTP 사용을 켜 두세요.</li>
              <li>저장 전 연결 테스트·테스트 메일 발송을 권장합니다.</li>
              {isEdit && <li>비밀번호 칸을 비우면 기존 비밀번호가 유지됩니다.</li>}
            </ul>
          </div>

          {formError && <div className="alert alert-danger py-2 small">{formError}</div>}
          {testMsg.ok === true && testMsg.text && (
            <div className="alert alert-success py-2 small">{testMsg.text}</div>
          )}

          <div className="row g-3">
            <div className="col-12">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-alias">
                  이름 (alias) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="smtp-alias"
                  type="text"
                  className={`form-control${validated && !form.alias.trim() ? ' is-invalid' : ''}`}
                  value={form.alias}
                  onChange={onChange('alias')}
                  placeholder="예: Gmail 발송용, 사내 SMTP"
                  autoComplete="off"
                />
                <div className="invalid-feedback">필수 항목입니다.</div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-host-preset">
                  호스트 <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  id="smtp-host-preset"
                  className="form-select mb-2"
                  value={hostPreset}
                  onChange={onHostPresetChange}
                >
                  {SMTP_HOST_PRESETS.map((p) => (
                    <option key={p.value || 'custom'} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <input
                  id="smtp-host"
                  type="text"
                  className={`form-control${validated && !form.host.trim() ? ' is-invalid' : ''}`}
                  value={form.host}
                  onChange={onChange('host')}
                  placeholder={hostPreset ? '예시에서 자동 입력됩니다' : 'smtp.example.com'}
                  autoComplete="off"
                  readOnly={Boolean(hostPreset)}
                />
                <div className="form-text">
                  {hostPreset
                    ? '예시 선택 시 호스트·포트·STARTTLS가 자동 입력됩니다. 직접 수정하려면 「직접 입력」을 선택하세요.'
                    : '호스트 주소를 직접 입력하세요.'}
                </div>
                <div className="invalid-feedback">필수 항목입니다.</div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-port">포트</label>
                <input
                  id="smtp-port"
                  type="number"
                  className="form-control"
                  value={form.port}
                  onChange={onChange('port')}
                  placeholder="587"
                  min={1}
                  max={65535}
                />
                <div className="form-text">587 / 465 / 25</div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-username">
                  발송 계정 (이메일) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="smtp-username"
                  type="email"
                  className={`form-control${validated && !form.username.trim() ? ' is-invalid' : ''}`}
                  value={form.username}
                  onChange={onChange('username')}
                  placeholder="sender@example.com"
                  autoComplete="off"
                />
                <div className="invalid-feedback">필수 항목입니다.</div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-password">
                  비밀번호
                  {isEdit && <span className="text-muted small ms-1">(비우면 유지)</span>}
                </label>
                <input
                  id="smtp-password"
                  type="password"
                  className="form-control"
                  value={form.password}
                  onChange={onChange('password')}
                  placeholder={isEdit ? '변경 시에만 입력' : '비밀번호 입력'}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-sender-name">발신자명</label>
                <input
                  id="smtp-sender-name"
                  type="text"
                  className="form-control"
                  value={form.senderName}
                  onChange={onChange('senderName')}
                  placeholder="예: 플랫폼 알림"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="single-input-item mb-0">
                <label htmlFor="smtp-timeout">연결 타임아웃 (ms)</label>
                <input
                  id="smtp-timeout"
                  type="number"
                  className="form-control"
                  value={form.connectionTimeoutMs}
                  onChange={onChange('connectionTimeoutMs')}
                  min={1000}
                  max={120000}
                  step={1000}
                />
              </div>
            </div>

            <div className="col-12">
              <div className="d-flex gap-4 pt-1">
                <div className="form-check">
                  <input
                    id="smtp-starttls"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.starttlsEnable}
                    onChange={onChange('starttlsEnable')}
                  />
                  <label className="form-check-label" htmlFor="smtp-starttls">STARTTLS 사용</label>
                </div>
                <div className="form-check">
                  <input
                    id="smtp-auth"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.smtpAuth}
                    onChange={onChange('smtpAuth')}
                  />
                  <label className="form-check-label" htmlFor="smtp-auth">SMTP 인증 사용</label>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="single-input-item mb-0 smtp-test-mail-box">
                <label htmlFor="smtp-test-to">테스트 메일 수신 주소</label>
                <div className="d-flex gap-2 flex-wrap align-items-stretch">
                  <input
                    id="smtp-test-to"
                    type="email"
                    className="form-control"
                    style={{ flex: '1 1 220px', minWidth: 0 }}
                    value={testToEmail}
                    onChange={(e) => setTestToEmail(e.target.value)}
                    placeholder="수신할 이메일 주소"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary smtp-footer-btn"
                    disabled={saving || testing || sendingTestMail}
                    onClick={handleSendTestMail}
                  >
                    {sendingTestMail ? '발송 중...' : '테스트 메일 발송'}
                  </button>
                </div>
                <div className="form-text">
                  현재 입력한 SMTP 설정으로 실제 메일을 보내 발송까지 확인합니다.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 푸터: 좌측 취소(+삭제), 우측 액션 버튼들 */}
      <div className="main-popup-footer">
        {/* 취소 — main-popup-left-btn 으로 좌측 고정 */}
        <button
          type="button"
          className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
          disabled={Boolean(confirmKind) || confirmBusy}
          onClick={() => onClose(false)}
        >
          취소
        </button>

        {/* 삭제 (비활성 설정 수정 시만) */}
        {isEdit && !isActive && (
          <button
            type="button"
            className="btn btn-outline-danger smtp-footer-btn"
            disabled={Boolean(confirmKind) || confirmBusy}
            onClick={handleDeleteClick}
          >
            삭제
          </button>
        )}

        {/* 활성 전환 (비활성 설정 수정 시만) */}
        {isEdit && !isActive && (
          <button
            type="button"
            className="btn btn-outline-primary smtp-footer-btn"
            disabled={Boolean(confirmKind) || confirmBusy}
            onClick={handleActivateClick}
          >
            활성 전환
          </button>
        )}

        {/* 연결 테스트 */}
        <button
          type="button"
          className="btn btn-outline-primary smtp-footer-btn"
          disabled={saving || testing || sendingTestMail || Boolean(confirmKind) || confirmBusy}
          onClick={handleTest}
        >
          {testing ? '테스트 중...' : '연결 테스트'}
        </button>

        {/* 저장 — main-popup-right-btn 으로 우측 주 버튼 */}
        <button
          type="button"
          className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`}
          disabled={saving || testing || sendingTestMail || Boolean(confirmKind) || confirmBusy}
          onClick={handleSave}
        >
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>

      {confirmKind ? (
        <div
          className="smtp-inline-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="smtp-inline-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleConfirmCancel();
          }}
        >
          <div className="smtp-inline-confirm-panel popup-text shadow-sm" onClick={(e) => e.stopPropagation()}>
            <h4 id="smtp-inline-confirm-title" className="h6 mb-3">
              {confirmTitle}
            </h4>
            <div className="mb-3" style={{ whiteSpace: 'pre-line' }}>
              {confirmContent}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className={`btn ${getButtonClass('btn-secondary')}`}
                disabled={confirmBusy}
                onClick={handleConfirmCancel}
              >
                취소
              </button>
              <button
                type="button"
                className={`btn ${getButtonClass(confirmPrimaryClass)}`}
                disabled={confirmBusy}
                onClick={handleConfirmApply}
              >
                {confirmBusy ? '처리 중...' : confirmPrimaryLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .popup-smtp-settings-form .smtp-footer-btn {
          height: 35px;
          padding: 0 14px;
          font-size: 0.875rem;
          white-space: nowrap;
        }
        .popup-smtp-settings-form .form-control:focus {
          border-color: var(--theme-color-primary, #e97730);
          box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--theme-color-primary, #e97730) 20%, transparent);
        }
        .popup-smtp-settings-form .form-control[readonly] {
          background-color: #f3f4f6;
          color: #6b7280;
          cursor: default;
        }
        .popup-smtp-settings-form .single-input-item label {
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 0.35rem;
          display: block;
        }
        .popup-smtp-settings-form .smtp-inline-confirm-overlay {
          position: absolute;
          inset: 0;
          z-index: 20;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: rgba(15, 23, 42, 0.35);
          border-radius: inherit;
        }
        .popup-smtp-settings-form .smtp-inline-confirm-panel {
          width: 100%;
          max-width: 420px;
          background: #fff;
          border-radius: 8px;
          padding: 20px;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default SmtpSettingsFormPopup;
